import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { ArrowBackRounded as ArrowBackIcon, RefreshRounded as RefreshIcon } from '@mui/icons-material';

import api from '../../services/api';
import AnnualLeaveSpotlight from '../../components/Dashboard/AnnualLeaveSpotlight';
import LeaveBalancesPanel from '../../components/Dashboard/LeaveBalancesPanel';
import { EmptyState, LoadingState, PageHeader, PageLayout, SectionCard, StatusBadge } from '../../components/ui';
import { normalizeRecentRequestRows } from './utils/recentRequestsTable';

const safeArray = (m) => (Array.isArray(m) ? m : []);

const formatDate = (d) => {
  if (!d) return '-';
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? String(d) : dt.toLocaleDateString();
};

export default function AdminMyLeave() {
  const theme = useTheme();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [fetching, setFetching] = useState(true);
  const [error, setError] = useState('');

  async function load() {
    setFetching(true);
    setError('');
    try {
      const res = await api.get('/admin/my-leave');
      setData(res.data || {});
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.message || 'Failed to load your leave.');
      setData(null);
    } finally {
      setFetching(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  const contractType = data?.employee?.contract_type || 'Unknown';
  const balances = useMemo(() => safeArray(data?.vacations), [data]);
  const annualBalance = useMemo(() => balances.find((b) => b?.leave_type === 'Annual') || null, [balances]);
  const recentRows = useMemo(
    () => normalizeRecentRequestRows(safeArray(data?.recent_requests), formatDate).slice(0, 8),
    [data]
  );
  const totalBalance = useMemo(
    () => balances.reduce((sum, b) => sum + (parseFloat(b?.current_balance) || 0), 0),
    [balances]
  );

  if (fetching) {
    return (
      <PageLayout backgroundAccent compact>
        <LoadingState rows={7} />
      </PageLayout>
    );
  }

  return (
    <PageLayout backgroundAccent compact>
      <PageHeader
        compact
        overline="HR admin"
        title="My leave"
        subtitle="Your own balances and recent requests, kept separate from the company console."
        actions={[
          <Button key="back" variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate('/admin/dashboard')}>
            Dashboard
          </Button>,
          <Button key="refresh" variant="outlined" startIcon={<RefreshIcon />} onClick={load}>
            Refresh
          </Button>,
        ]}
      />

      {error ? (
        <EmptyState title="Could not load your leave" description={error} actionLabel="Retry" onAction={load} />
      ) : null}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 0.9fr) minmax(0, 1.1fr)' }, gap: 1.4, alignItems: 'start' }}>
        <SectionCard
          title="Annual leave"
          subheader={annualBalance ? `${Number(annualBalance.current_balance || 0).toFixed(1)} days available` : 'No annual balance configured'}
          tone="dashboard"
          sx={{ height: '100%', borderRadius: '12px' }}
        >
          <AnnualLeaveSpotlight annualBalance={annualBalance} contractType={contractType} />
        </SectionCard>

        <SectionCard
          title="My leave balances"
          subheader={`${balances.length} leave type(s) · ${totalBalance.toFixed(1)} days total`}
          tone="dashboard"
          sx={{ height: '100%', borderRadius: '12px' }}
        >
          <LeaveBalancesPanel
            balances={balances}
            contractType={contractType}
            emptyTitle="No balances"
            emptyDescription="No leave balances are available for your profile."
          />
        </SectionCard>
      </Box>

      <SectionCard
        title="My recent requests"
        subheader={`${recentRows.length} latest request(s)`}
        tone="dashboard"
        sx={{ mt: 1.4, borderRadius: '12px' }}
      >
        {recentRows.length ? (
          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: '10px' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ '& .MuiTableCell-head': { fontWeight: 700, color: 'text.secondary', backgroundColor: alpha(theme.palette.primary.main, 0.04) } }}>
                  <TableCell>Type</TableCell>
                  <TableCell>Start</TableCell>
                  <TableCell>End</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>HR</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recentRows.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>{row.leaveTypeDisplay}</TableCell>
                    <TableCell sx={{ fontVariantNumeric: 'tabular-nums' }}>{row.startDateDisplay}</TableCell>
                    <TableCell sx={{ fontVariantNumeric: 'tabular-nums' }}>{row.endDateDisplay}</TableCell>
                    <TableCell>
                      <StatusBadge status={row.statusDisplay || '-'} />
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={row.hrStatusDisplay || '-'} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <EmptyState compact title="No recent requests" description="You have no recent personal requests." />
        )}
      </SectionCard>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.2, px: 0.5 }}>
        Submit new leave from the requests area in the sidebar.
      </Typography>
    </PageLayout>
  );
}
